export default class SimpleCarousel {

  #curIndex = 0;
  #max = 0;
  #maxVisible = 0;
  #length = 0;
  #defaultDuration = 400;
  #controller;
  duration;
  #timeThreshhold = 100;
  #swipeThreshhold = 50;
  #tapThreshhold = 15;
  #tapDuration = 300;
  #swipeStart = { x: 0, y: 0 };
  #swipeTimeBegin = 0;
  #swiping = false;

  /**
    * Carousel container element must include:
    * 
    *  - Slides Container: HTMLDivElement, selector: [data-carousel-slides]
    * 
    * Optional: use one or both (nav or index):
    * 
    *  - Nav Buttons: HTMLButtonElement(s), selectors: [data-carousel-btn][data-dir="left"], [data-carousel-btn][data-dir="right"]
    * 
    *  - Index Button Container: HTMLDivElement, selector: [data-carousel-index-btns], empty: buttons created via JS
   * @param {HTMLElement} container - carousel container element
   * @param {number} [duration] - (opional) number in milliseconds, required if transition-duration/transition is not set on slides container
   * @param {string} [indexBtnClass] - (optional) class to give index buttons
   * @param {{ tapAdvance?: boolean }} [options] - (optional) gesture behavior config
   */
  constructor(container, duration, indexBtnClass, options = {}) {
    /* console.log('new SimpleCarousel'); */
    this.container = container;
    container.dataset.active = 'true';
    //
    this.duration = duration;
    this.options = { tapAdvance: false, ...options };
    //
    this.slides = this.container.querySelector('[data-carousel-slides]');
    this.touchSurface = this.slides.parentElement;
    if (this.duration)  {
      this.slides.style.transition = `transform ${this.duration}ms ease-out`;
      this.slides.style.transform = 'translateX(0%)';
    }
    //
    this.#length = this.slides.children.length;
    this.#max = this.#length - 1;
    this.#maxVisible = Math.floor(this.#length / 2);
    //
    this.clones = [
      this.slides.children[0].cloneNode(true),
      this.slides.children[1].cloneNode(true),
      this.slides.children[this.#max].cloneNode(true),
      this.slides.children[this.#max - 1].cloneNode(true),
    ];
    this.clones[2].style.transform = `translateX(${(this.#length + 3) * -100}%)`;
    this.clones[3].style.transform = `translateX(${(this.#length + 5) * -100}%)`;
    this.clones.forEach((el) => {
      el.dataset.clone = 'true';
      this.slides.appendChild(el);
    });
    //
    this.btns = this.container.querySelectorAll('[data-carousel-btn][data-dir]');
    this.indexBtnContainer = this.container.querySelector('[data-carousel-index-btns]');
    this.indexBtns = [];
    //
    this.#init(indexBtnClass);
  }

  #init(indexBtnClass) {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    //
    this.btns.forEach((btn) => {
      btn.addEventListener('click', (e) => this.#onDirBtn(e.target.dataset.dir), { signal });
    });
    //
    if (this.indexBtnContainer) {
      for (let i = 0; i < this.#length; i++) {
        const btn = document.createElement('BUTTON');
        btn.dataset.carouselDot = 'true';
        btn.dataset.index = i;
        btn.dataset.active = i ? 'false' : 'true';
        btn.title = `go to slide ${i + 1}`;
        if (indexBtnClass) btn.className = indexBtnClass;
        btn.addEventListener('click', (e) => this.#onIndexBtn(parseInt(e.target.dataset.index), { signal }));
        this.indexBtnContainer.appendChild(btn);
        this.indexBtns.push(btn)
      };
    }
    //
    this.touchSurface.addEventListener('touchstart', (e) => this.#onTouchStart(e), { signal });
    this.touchSurface.addEventListener('touchend', (e) => this.#onTouchEnd(e), { signal });
    this.touchSurface.addEventListener('touchmove', (e) => this.#onTouchMove(e), { signal });
  }

  #onDirBtn(dir) {
    if (this.slides.style.transitionDuration === '0s') return;
    const index = dir === 'right' ? this.#curIndex + 1 : this.#curIndex - 1;
    const idx = index > this.#max ? 0 : (index < 0 ? this.#max : index);
    this.#updateIndexBtns(idx);
    //
    if (index > this.#max) {
      setTimeout(() => {
        this.#curIndex = 0;
        this.slides.style.transitionDuration = '0s';
        this.slides.style.transform = 'translate3d(0%,0,0)';
        setTimeout(() => {
          this.slides.style.transitionDuration = (this.duration ? `${this.duration}ms` : null);
        }, 40);
      }, this.duration || this.#defaultDuration);
    } else if (index === -1) {
      setTimeout(() => {
        this.#curIndex = this.#max;
        this.slides.style.transitionDuration = '0s';
        this.slides.style.transform = `translate3d(${this.#curIndex * -100}%,0,0)`;
        setTimeout(() => {
          this.slides.style.transitionDuration = (this.duration ? `${this.duration}ms` : null);
        }, 40);
      }, this.duration || this.#defaultDuration);
    }
    //
    this.#curIndex = index;
    this.slides.style.transform = `translate3d(${this.#curIndex * -100}%,0,0)`;
  }

  #onIndexBtn(index) {
    this.#curIndex = index;
    this.#updateIndexBtns(this.#curIndex);
    this.slides.style.transform = `translate3d(${this.#curIndex * -100}%,0,0)`;
  }

  #updateIndexBtns(index) {
    if (this.indexBtns.length) {
      this.indexBtns.forEach((btn) => btn.dataset.active = 'false');
      this.indexBtns[index].dataset.active = 'true';
    }
  }

  #onTouchStart(e) {
    this.touchSurface.style.transitionDuration = '0s';
    this.#swipeStart = { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
    this.#swipeTimeBegin = e.timeStamp;
    this.#swiping = true;
  }

  #onTouchEnd(e) {
    this.#swiping = false;
    this.touchSurface.style.transitionDuration = '0.24s';
    this.touchSurface.style.transform = `translate3d(0%,0,0)`;
    const amountX = this.#swipeStart.x - e.changedTouches[0].clientX;
    const amountY = this.#swipeStart.y - e.changedTouches[0].clientY;
    const time = e.timeStamp - this.#swipeTimeBegin;
    const absX = Math.abs(amountX);
    const absY = Math.abs(amountY);

    if (time > this.#timeThreshhold && absX >= this.#swipeThreshhold && absX > absY) {
      this.#onDirBtn(amountX > 0 ? 'right' : 'left');
      this.#resetTouchState();
      return;
    }

    if (
      this.options.tapAdvance &&
      time <= this.#tapDuration &&
      absX <= this.#tapThreshhold &&
      absY <= this.#tapThreshhold
    ) {
      this.#onDirBtn('right');
    }

    this.#resetTouchState();
  }

  #onTouchMove(e) {
    if (this.#swiping) {
      const amountX = this.#swipeStart.x - e.changedTouches[0].clientX;
      const amountY = this.#swipeStart.y - e.changedTouches[0].clientY;
      const absX = Math.abs(amountX);
      const absY = Math.abs(amountY);

      if (absX > absY) {
        const percent = Math.round(amountX) / 10;
        if (absX >= 1) e.preventDefault();
        this.touchSurface.style.transform = `translate3d(${-percent}%,0,0)`;
        return;
      }

      this.touchSurface.style.transform = `translate3d(0%,0,0)`;
    }
  }

  #resetTouchState() {
    this.#swipeStart = { x: 0, y: 0 };
    this.#swipeTimeBegin = 0;
  }

  kill() {
    this.#controller.abort();
    this.clones.forEach((el) => el.parentElement.removeChild(el));
    this.indexBtns.forEach((btn) => btn.parentElement.removeChild(btn));
    this.slides.children.forEach((s) => s.style = undefined);
    this.slides.style = undefined;
    this.touchSurface.style = undefined;
  }
}
